/**
 * B2B Link Tracking for GTM
 * Tracks link and button clicks on hero-discounts and group-order page templates
 * Sends b2b_link_click events to GTM with custom parameters
 */

class B2BLinkTracking {
  constructor() {
    // Prevent multiple instances
    if (window.b2bLinkTrackingInstance) {
      return window.b2bLinkTrackingInstance;
    }
    
    this.targetTemplates = ['page.hero-discounts', 'page.group-order'];
    this.currentTemplate = window.template || '';
    this.dataLayer = window.dataLayer || [];
    this.isInitialized = false;
    
    // Check if we're on a target page template
    if (this.isTargetTemplate()) {
      this.init();
    }
    
    // Store instance globally
    window.b2bLinkTrackingInstance = this;
  }

  /**
   * Check if current page template is one of the target templates
   */
  isTargetTemplate() {
    return this.targetTemplates.some(template => this.currentTemplate.includes(template));
  }

  /**
   * Initialize link tracking
   */
  init() {
    if (this.isInitialized) {
      return;
    }
    
    this.setupClickTracking();
    this.isInitialized = true;
  }

  /**
   * Setup unified click tracking for links and buttons
   */
  setupClickTracking() {
    document.addEventListener('click', (event) => {
      this.handleClick(event);
    });
  }

  /**
   * Handle click events and determine if it's a link or button
   */
  handleClick(event) {
    const target = event.target;
    
    // Find the closest link or button
    const link = this.findClosestLink(target);
    const button = this.findClosestButton(target);
    
    // Track as link if it's a link (prioritize link over button)
    if (link) {
      this.trackLinkClick(link, event);
      return;
    }
    
    // Track as button if it's a button
    if (button) {
      this.trackButtonClick(button, event);
      return;
    }
  }

  /**
   * Find the closest link element to the clicked target
   */
  findClosestLink(element) {
    if (!element) return null;
    
    // Check if the element itself is a link
    if (element.tagName === 'A' && element.href) {
      return element;
    }
    
    // Check if the element is inside a link
    const link = element.closest('a[href]');
    if (link && link.href) {
      return link;
    }
    
    return null;
  }

  /**
   * Find the closest button element to the clicked target
   */
  findClosestButton(element) {
    if (!element) return null;
    
    // Check if the element itself is a button
    if (element.tagName === 'BUTTON' || 
        (element.tagName === 'A' && element.classList.contains('button'))) {
      return element;
    }
    
    // Check if the element is inside a button
    const button = element.closest('button, a.button');
    if (button) {
      return button;
    }
    
    return null;
  }

  /**
   * Track link click and send to GTM
   */
  trackLinkClick(link, event) {
    const linkText = this.getLinkText(link);
    const destinationUrl = link.href;
    const sourcePage = window.location.pathname;
    const sourceTemplate = this.currentTemplate;

    // Prevent tracking of internal navigation links that don't lead to external sites
    if (this.isInternalNavigation(destinationUrl)) {
      return;
    }

    // Send event to GTM
    this.sendB2BLinkClickEvent({
      link_text: linkText,
      destination_url: destinationUrl,
      source_page: sourcePage,
      source_template: sourceTemplate,
      element_type: 'link'
    });
  }

  /**
   * Track button click and send to GTM
   */
  trackButtonClick(button, event) {
    const buttonText = this.getButtonText(button);
    const destinationUrl = this.getButtonDestination(button);
    const sourcePage = window.location.pathname;
    const sourceTemplate = this.currentTemplate;

    // Send event to GTM
    this.sendB2BLinkClickEvent({
      link_text: buttonText,
      destination_url: destinationUrl,
      source_page: sourcePage,
      source_template: sourceTemplate,
      element_type: 'button'
    });
  }

  /**
   * Get the text content of a link
   */
  getLinkText(link) {
    // Get text content, removing extra whitespace
    let text = link.textContent || link.innerText || '';
    text = text.trim().replace(/\s+/g, ' ');
    
    // If no text, try to get alt text from image
    if (!text && link.querySelector('img')) {
      text = link.querySelector('img').alt || '';
    }
    
    // If still no text, use href as fallback
    if (!text) {
      text = link.href;
    }
    
    return text || 'Unknown Link';
  }

  /**
   * Get the text content of a button
   */
  getButtonText(button) {
    // Get text content, removing extra whitespace
    let text = button.textContent || button.innerText || '';
    text = text.trim().replace(/\s+/g, ' ');
    
    // If no text, try to get value attribute
    if (!text) {
      text = button.value || '';
    }
    
    // If still no text, try to get aria-label
    if (!text) {
      text = button.getAttribute('aria-label') || '';
    }
    
    return text || 'Unknown Button';
  }

  /**
   * Get the destination URL for a button
   */
  getButtonDestination(button) {
    // If it's a link styled as a button
    if (button.tagName === 'A' && button.href) {
      return button.href;
    }
    
    // If it's a form submit button
    if (button.type === 'submit') {
      const form = button.closest('form');
      if (form && form.action) {
        return form.action;
      }
    }
    
    // Check for data attributes that might contain URLs
    const dataUrl = button.dataset.url || button.dataset.href || button.dataset.link;
    if (dataUrl) {
      return dataUrl;
    }
    
    // Default to current page if no destination found
    return window.location.href;
  }

  /**
   * Check if the URL is internal navigation that shouldn't be tracked
   */
  isInternalNavigation(url) {
    // Don't track anchor links
    if (url.startsWith('#')) {
      return true;
    }
    
    // Don't track javascript: links
    if (url.startsWith('javascript:')) {
      return true;
    }
    
    // Don't track mailto: links
    if (url.startsWith('mailto:')) {
      return true;
    }
    
    // Don't track tel: links
    if (url.startsWith('tel:')) {
      return true;
    }
    
    // Allow tracking of relative URLs (like /collections/mission)
    // Only skip if it's the same page or truly internal navigation
    if (url === window.location.pathname || url === window.location.href) {
      return true;
    }
    
    return false;
  }

  /**
   * Send b2b_link_click event to GTM dataLayer
   */
  sendB2BLinkClickEvent(data) {
    const eventData = {
      event: 'b2b_link_click',
      link_text: data.link_text,
      destination_url: data.destination_url,
      source_page: data.source_page,
      source_template: data.source_template,
      element_type: data.element_type,
      timestamp: new Date().toISOString(),
      page_url: window.location.href,
      page_title: document.title
    };

    // Push to dataLayer
    this.dataLayer.push(eventData);
  }
}

// Initialize B2B Link Tracking only once
if (!window.b2bLinkTrackingInstance) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      new B2BLinkTracking();
    });
  } else {
    new B2BLinkTracking();
  }
} 